/**
 * React hook for connecting to a lobby's WebSocket. Returns the latest
 * lobby state, game state, private state, and a `send` function.
 */

import { useEffect, useRef, useState } from "react";
import type {
  ClientMsg,
  LobbyDetail,
  PrivateGameData,
  PublicGameState,
  ServerMsg,
  GameEvent,
  GameSummary,
} from "../../../shared/protocol.js";
import { openWs, type WsHandle } from "./ws.js";

export interface ChatMessage {
  from: string;
  fromName: string;
  text: string;
  at: number;
}

/** Max chat messages retained client-side. Older messages drop off the top. */
const CHAT_HISTORY_LIMIT = 100;

export interface LobbyWsState {
  connected: boolean;
  joined: boolean;
  lobby: LobbyDetail | null;
  gameState: PublicGameState | null;
  yourSeat: number | null;
  privateData: PrivateGameData | null;
  events: GameEvent[];
  chat: ChatMessage[];
  summary: GameSummary | null;
  lastError: string | null;
  /** Set when the lobby has been closed by the server (e.g. host disconnected). */
  closedReason: string | null;
}

export function useLobbyWs(lobbyId: string | null): LobbyWsState & { send: (m: ClientMsg) => void } {
  const [state, setState] = useState<LobbyWsState>({
    connected: false,
    joined: false,
    lobby: null,
    gameState: null,
    yourSeat: null,
    privateData: null,
    events: [],
    chat: [],
    summary: null,
    lastError: null,
    closedReason: null,
  });
  const wsRef = useRef<WsHandle | null>(null);

  useEffect(() => {
    if (!lobbyId) return;
    const handle = openWs(lobbyId, {
      onOpen: () => setState((s) => ({ ...s, connected: true })),
      onClose: () => setState((s) => ({ ...s, connected: false })),
      onMessage: (msg: ServerMsg) => {
        switch (msg.t) {
          case "joined":
            setState((s) => ({ ...s, joined: true }));
            break;
          case "lobby":
            setState((s) => ({ ...s, lobby: msg.lobby }));
            break;
          case "game_state":
            setState((s) => ({ ...s, gameState: msg.state, yourSeat: msg.yourSeat }));
            break;
          case "private_state":
            setState((s) => ({ ...s, privateData: msg.data }));
            break;
          case "game_event":
            setState((s) => ({
              ...s,
              events: [...s.events.slice(-49), msg.event],
            }));
            break;
          case "lobby_chat":
            setState((s) => ({
              ...s,
              chat: [
                ...s.chat.slice(-(CHAT_HISTORY_LIMIT - 1)),
                { from: msg.from, fromName: msg.fromName, text: msg.text, at: msg.at },
              ],
            }));
            break;
          case "game_started":
            setState((s) => ({ ...s, summary: null, events: [] }));
            break;
          case "game_ended":
            setState((s) => ({ ...s, summary: msg.summary }));
            break;
          case "error":
            // `no_lobby` means the user opened a stale URL/bookmark for
            // a lobby that's been reaped (host disconnect, inactivity,
            // server restart). Treat it like a closed lobby so the
            // existing `closedReason` redirect kicks in and bounces
            // them back home instead of leaving a stuck toast.
            if (msg.code === "no_lobby") {
              setState((s) => ({ ...s, closedReason: "Lobby no longer exists" }));
              break;
            }
            setState((s) => ({ ...s, lastError: msg.message }));
            // Clear error after a beat so a follow-up shows fresh.
            setTimeout(() => setState((s2) => ({ ...s2, lastError: null })), 3500);
            break;
          case "lobby_closed":
            setState((s) => ({ ...s, closedReason: msg.reason }));
            break;
        }
      },
    });
    wsRef.current = handle;
    return () => {
      handle.close();
      wsRef.current = null;
    };
  }, [lobbyId]);

  function send(msg: ClientMsg) {
    wsRef.current?.send(msg);
  }

  return { ...state, send };
}
